import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TENANT_STATUS_MAP, type TenantStatus } from '@goyone/shared';
import { date, money } from '@ui/lib/format';
import { Badge, ErrorState, PageHead, Stat } from '@ui/components/ui';
import { api } from '../lib/api';

interface Summary {
  tenants: Partial<Record<TenantStatus, number>>;
  total: number;
  mrr: number;
  byPlan: Array<{ planId: string; name: string; count: number; revenue: number }>;
  recent: Array<{
    id: string;
    slug: string;
    name: string;
    status: TenantStatus;
    createdAt: string;
  }>;
}

export function Overview() {
  const summary = useQuery({
    queryKey: ['summary'],
    queryFn: () => api<Summary>('/summary'),
  });

  const s = summary.data;
  const paying = (s?.tenants.ACTIVE ?? 0) + (s?.tenants.PAST_DUE ?? 0);
  const largest = s ? Math.max(...s.byPlan.map((p) => p.revenue), 1) : 1;

  return (
    <div className="page">
      <PageHead title="نظرة عامة" subtitle="حالة المنصة وإيرادها" />

      {summary.isError && (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      )}

      <div className="stats">
        <Stat
          label="الإيراد الشهري المتكرر"
          value={<span className="num">{s ? money(s.mrr) : '—'}</span>}
          note="الاشتراكات النشطة والمتأخرة"
        />
        <Stat label="إجمالي المستأجرين" value={s?.total ?? '—'} />
        <Stat
          label="مشتركون يدفعون"
          value={paying || '—'}
          note={s ? `${s.tenants.TRIAL ?? 0} في الفترة التجريبية` : undefined}
        />
        <Stat
          label="يحتاجون متابعة"
          value={
            <span className={(s?.tenants.PAST_DUE ?? 0) > 0 ? 'neg' : ''}>
              {(s?.tenants.PAST_DUE ?? 0) + (s?.tenants.SUSPENDED ?? 0) || '—'}
            </span>
          }
          note="متأخر السداد أو موقوف"
        />
      </div>

      <div className="builder">
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">التوزيع على الحالات</h2>
          </div>
          <div className="card-body">
            <div className="chart-bars">
              {Object.entries(TENANT_STATUS_MAP).map(([key, def]) => {
                const count = s?.tenants[key as TenantStatus] ?? 0;
                return (
                  <div key={key} className="chart-bar-row">
                    <div className="chart-bar-label">{def.label}</div>
                    <div className="chart-bar-track">
                      <div
                        className="chart-bar-fill"
                        style={{
                          width: `${s?.total ? (count / s.total) * 100 : 0}%`,
                          background: 'var(--brand)',
                        }}
                      />
                    </div>
                    <div className="chart-bar-value num">{count}</div>
                    <div className="chart-bar-share tiny mute num">
                      {s?.total ? Math.round((count / s.total) * 100) : 0}٪
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="card-title">الإيراد حسب الباقة</h2>
          </div>
          <div className="card-body">
            {s?.byPlan.length === 0 ? (
              <p className="small mute">لا اشتراكات بعد.</p>
            ) : (
              <div className="chart-bars">
                {s?.byPlan.map((p) => (
                  <div key={p.planId} className="chart-bar-row">
                    <div className="chart-bar-label">{p.name}</div>
                    <div className="chart-bar-track">
                      <div
                        className="chart-bar-fill"
                        style={{
                          width: `${(p.revenue / largest) * 100}%`,
                          background: 'var(--accent-600)',
                        }}
                      />
                    </div>
                    <div className="chart-bar-value num">{money(p.revenue)}</div>
                    <div className="chart-bar-share tiny mute num">{p.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">أحدث المستأجرين</h2>
          <Link to="/tenants" className="btn btn-sm">
            عرض الكل
          </Link>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>النطاق</th>
                <th>الاسم</th>
                <th>الحالة</th>
                <th>تاريخ الإنشاء</th>
              </tr>
            </thead>
            <tbody>
              {s?.recent.map((t) => (
                <tr key={t.id}>
                  <td className="num">
                    <Link to={`/tenants/${t.id}`} className="link cell-strong">
                      {t.slug}
                    </Link>
                  </td>
                  <td>{t.name}</td>
                  <td>
                    <Badge kind={TENANT_STATUS_MAP[t.status].kind}>
                      {TENANT_STATUS_MAP[t.status].label}
                    </Badge>
                  </td>
                  <td className="num cell-mute">{date(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
